import { useEffect, useMemo } from 'react'
import { addDays, startOfWeek, today } from '@/logic/dates'
import { matchesEventFilter, ownerOf, type CalEvent } from '@/logic/events'
import { useAuth } from '@/store/auth'
import { checkConnection, hydrateFromCache, loadEvents, useCalendar } from '@/store/calendar'
import { useHousehold } from '@/store/household'
import { usePrefs } from '@/store/prefs'
import type { CalendarOwner, DateStr } from '@/types'

export interface ShownEvent extends CalEvent {
  owner: CalendarOwner
}

/**
 * Keeps Google events loaded for a range: from the device's cache at once, then
 * from Google when the view opens, the range changes, the window regains focus
 * and every five minutes. The range always takes in this week and next, so the
 * Plan tab has its events offline whichever view fetched last.
 */
export function useEventWindow(from: DateStr, to: DateStr): void {
  const settings = useHousehold((s) => s.userDoc?.settings)
  const connection = useCalendar((s) => s.connection)
  const monday = startOfWeek(today())
  const lo = from < monday ? from : monday
  const hi = to > addDays(monday, 13) ? to : addDays(monday, 13)

  useEffect(() => {
    void hydrateFromCache(settings)
    if (useCalendar.getState().connection === 'unknown') void checkConnection()
    // settings arrive once; hydrating again on every change would be noise
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (connection !== 'connected') return
    void loadEvents(lo, hi, settings)
    const onFocus = () => {
      if (document.visibilityState === 'visible') void loadEvents(lo, hi, settings, true)
    }
    globalThis.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onFocus)
    const timer = globalThis.setInterval(() => void loadEvents(lo, hi, settings, true), 5 * 60_000)
    return () => {
      globalThis.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onFocus)
      globalThis.clearInterval(timer)
    }
  }, [connection, lo, hi, settings])
}

/** The loaded events that pass the Whose filter, each with whose calendar it is on. Category never hides events. */
export function useShownEvents(): ShownEvent[] {
  const email = useAuth((s) => s.user?.email ?? null)
  const settings = useHousehold((s) => s.userDoc?.settings)
  const otherEmail = useHousehold((s) => s.household?.memberEmails.find((e) => e !== email) ?? null)
  const whose = usePrefs((s) => s.filter.whose)
  const calendars = useCalendar((s) => s.calendars)
  const events = useCalendar((s) => s.events)
  return useMemo(() => {
    const owners = new Map(calendars.map((c) => [c.id, ownerOf(c, settings, otherEmail)]))
    const out: ShownEvent[] = []
    for (const ev of events) {
      const owner = owners.get(ev.calendarId) ?? 'both'
      if (matchesEventFilter(owner, whose)) out.push({ ...ev, owner })
    }
    return out
  }, [events, calendars, settings, otherEmail, whose])
}
